import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';
import {Product, ProductRange} from '../../../interfaces/catalog-interfaces';
import {getAllItems, getItemsByCategory, getItemsByResearch} from '../../../services/catalog-services';
import styles from './items-list.module.scss';
import {I18n, useTranslation, withTranslation} from 'next-i18next';
import {connect} from 'react-redux';
import {ShopState} from '../../../interfaces/store-interfaces';
import {addToCart} from '../../../store/action';
import Loader from '../../loader/loader';
import ItemProduct from "./items/item-product";
import ItemRange from "./items/item-range";

// --- Persistance du catalogue pour la restauration du scroll ---
// La liste produits est chargée en "load more" dans un state React éphémère.
// Sans persistance, un retour arrière depuis une fiche produit remonte le
// composant, vide la liste et ramène l'utilisateur en haut de page.
// On mémorise donc la liste chargée + la position de scroll dans sessionStorage,
// keyée sur les filtres actifs, et on restaure au remontage.
const CATALOG_CACHE_PREFIX = 'catalog-list:';

type CatalogSnapshot = {
    items: (Product | ProductRange)[],
    page: number,
    nbProducts: number,
    scrollY: number
};

function buildCacheKey(p: { activeCategory: number, activeSorting: string, currentCurrency: string, researchQuery: string, activeTags: number[], i18n: I18n }): string {
    return CATALOG_CACHE_PREFIX + JSON.stringify({
        category: p.activeCategory,
        sorting: p.activeSorting,
        currency: p.currentCurrency,
        research: p.researchQuery || '',
        tags: [...p.activeTags].sort((a, b) => a - b),
        lang: p.i18n.language
    });
}

function readSnapshot(key: string): CatalogSnapshot | null {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

function writeSnapshot(key: string, value: CatalogSnapshot): void {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        // sessionStorage indisponible ou quota dépassé — la restauration est best-effort
    }
}

function Items(props: {
    activeCategory: number,
    activeSorting: string,
    currentCurrency: string,
    i18n: I18n,
    nbProducts: number,
    addToCart: any,
    shoppingCart: {},
    researchQuery: string,
    setNbProducts: any,
    activeTags: number[]
}) {

    const {t} = useTranslation('common')
    const router = useRouter();

    const cacheKey = buildCacheKey(props);
    // Lecture synchrone au premier rendu : si un instantané existe pour ces
    // filtres, on repart avec la liste déjà chargée (DOM à la bonne hauteur).
    const snapshotRef = useRef<CatalogSnapshot | null>(typeof window !== 'undefined' ? readSnapshot(cacheKey) : null);
    const snapshot = snapshotRef.current;

    const [items, setItems] = useState<(Product | ProductRange)[]>(snapshot ? snapshot.items : []);
    const [page, setPage] = useState<number>(snapshot ? snapshot.page : 0);
    const [isLoading, setLoad] = useState<Boolean>(snapshot ? false : true);
    const [error, setError] = useState<Boolean>(false);

    // Évite le re-fetch (et la duplication d'items) au montage quand on restaure.
    const skipInitialFetch = useRef<boolean>(!!snapshot);

    const fetchItems = async () => {
        setLoad(true);
        if (props.activeCategory === 0) {
            try {
                const products = await getAllItems(props.currentCurrency, (props.i18n.language).toUpperCase(), page, props.activeSorting)
                setItems(items.concat(products['data']))
                props.setNbProducts(+products['headers']['content-range']);
            } catch (e) {
                setError(true);
            }
        } else {
            try {
                const products = await getItemsByCategory(props.currentCurrency, (props.i18n.language).toUpperCase(), page, props.activeSorting, props.activeCategory)
                setItems(items.concat(products['data']));
                props.setNbProducts(+products['headers']['content-range']);
            } catch (e) {
                setError(true);
            }
        }
        setLoad(false);
    }

    const fetchProductsOfResearchResult = async () => {
        setLoad(true);
        try {
            const products = await getItemsByResearch(props.currentCurrency, (props.i18n.language).toUpperCase(), page, props.activeSorting, props.researchQuery, props.activeCategory, props.activeTags)
            setItems(items.concat(products['data']));
            props.setNbProducts(+products['headers']['content-range']);
        } catch (e) {
            setError(true)
        }
        setLoad(false);
    }

    const reloadProducts = () => {
        window.location.reload()
    }

    useEffect(() => {
        if (skipInitialFetch.current) {
            // Liste restaurée depuis sessionStorage : on ne refetch pas, on
            // resynchronise juste le total de produits (géré par le parent).
            skipInitialFetch.current = false;
            if (snapshot) props.setNbProducts(snapshot.nbProducts);
            return;
        }
        ((props.researchQuery !== undefined && props.researchQuery !== "") || props.activeTags.length > 0) ? fetchProductsOfResearchResult() : fetchItems();
    }, [page])

    // Restaure la position de scroll une fois la liste restaurée peinte.
    useLayoutEffect(() => {
        if (snapshot && typeof snapshot.scrollY === 'number') {
            window.scrollTo(0, snapshot.scrollY);
            // Nouvelle passe après stabilisation du layout (images du grid, etc.).
            requestAnimationFrame(() => window.scrollTo(0, snapshot.scrollY));
        }
        // Montage uniquement.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Refs pour lire les dernières valeurs dans le handler de navigation.
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const pageRef = useRef(page);
    pageRef.current = page;
    const nbProductsRef = useRef(props.nbProducts);
    nbProductsRef.current = props.nbProducts;

    // Sauvegarde l'instantané (liste + scroll) juste avant de quitter la page
    // (ex. clic sur une fiche produit), pour pouvoir restaurer au retour.
    useEffect(() => {
        const save = () => {
            if (itemsRef.current.length === 0) return;
            writeSnapshot(cacheKey, {
                items: itemsRef.current,
                page: pageRef.current,
                nbProducts: nbProductsRef.current,
                scrollY: window.scrollY
            });
        };
        router.events.on('routeChangeStart', save);
        return () => {
            router.events.off('routeChangeStart', save);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey])

    return (
        <>
            {
                error &&
                <div className={styles['error-load-product']}>
                    <p>{t('CATALOG_ERROR')}<span>☹</span></p>
                    <div className={styles['refresh']} onClick={reloadProducts}>
                        <img src="/assets/images/refresh.png" alt={"refresh"}/>
                    </div>
                </div>
            }
            <div className={styles['products-list']}>
                {
                    items.map((item) =>
                        item.hasOwnProperty("productId") ?
                            // @ts-ignore
                            <ItemProduct productItem={item}/> :
                            // @ts-ignore
                            <ItemRange rangeItem={item}/>
                    )
                }
            </div>
            {
                isLoading &&
                <Loader/>
            }
            {
                ((props.nbProducts - items.length > 0) && !error && !isLoading) &&
                <>

                    {

                        (props.nbProducts - items.length !== 1) ? (
                                <div className={styles['products-list-next']}
                                     onClick={() => setPage(page + 1)}>
                                    <p dangerouslySetInnerHTML={{__html: t('CATALOG_NEXT_PRODUCTS').replace('%NBPRODUITS%', "" + (props.nbProducts - items.length)).replaceAll('%SPAN%', '<span>').replaceAll('%!SPAN%', '</span>')}}/>
                                </div>
                            ) :
                            (
                                <div className={styles['products-list-next']}
                                     onClick={() => setPage(page + 1)}>
                                    <p dangerouslySetInnerHTML={{__html: t('CATALOG_LAST_PRODUCT').replace('%SPAN%', '<span>').replaceAll('%!SPAN%', '</span>')}}/>
                                </div>
                            )
                    }


                </>
            }
        </>
    )
}

const mapState = (state: ShopState) => (
    {
        currentCurrency: state.currentCurrency,
        shoppingCart: state.shoppingCart
    }
)

const mapDispatch = (dispatch) => {
    return {
        addToCart: (product: { idP: number, quantity: number }) => dispatch(addToCart(product))
    }
}

export default connect(mapState, mapDispatch)(withTranslation('common')(Items));
